import { useEffect, useState } from 'react';
import { listTotpFactors, enrollTotp, verifyTotp, disableTotp, type MfaFactor } from '../lib/mfa';

/**
 * Gestion de la 2FA (TOTP) pour le compte connecté.
 * Activation : QR à scanner (Google Authenticator, etc.) → code à 6 chiffres → vérifié.
 * Désactivation possible. Affiché dans la page de connexion quand on est connecté.
 */
export default function TwoFactorPanel() {
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [enroll, setEnroll] = useState<{ factorId: string; qrCode?: string; secret?: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true);
    listTotpFactors().then((f) => {
      setFactors(f);
      setLoading(false);
    });
  };
  useEffect(refresh, []);

  const verified = factors.find((f) => f.status === 'verified');

  const startEnroll = async () => {
    setError(null);
    setMsg(null);
    setBusy(true);
    const res = await enrollTotp();
    setBusy(false);
    if (!res.ok || !res.factorId) {
      setError(res.error ?? 'Activation impossible.');
      return;
    }
    setEnroll({ factorId: res.factorId, qrCode: res.qrCode, secret: res.secret });
  };

  const confirmEnroll = async () => {
    if (!enroll) return;
    setError(null);
    setBusy(true);
    const res = await verifyTotp(enroll.factorId, code);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Code invalide.');
      return;
    }
    setEnroll(null);
    setCode('');
    setMsg('✅ 2FA activée.');
    refresh();
  };

  const disable = async () => {
    if (!verified) return;
    if (!confirm('Désactiver la double authentification ?')) return;
    setBusy(true);
    const res = await disableTotp(verified.id);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Désactivation impossible.');
      return;
    }
    setMsg('2FA désactivée.');
    refresh();
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-200 p-3 text-sm">
      <div className="mb-1 font-semibold">🔐 Double authentification (2FA)</div>
      {loading ? (
        <p className="opacity-60">Chargement…</p>
      ) : verified && !enroll ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-green-700">✅ Activée sur ce compte.</span>
          <button onClick={disable} disabled={busy} className="underline text-red-600 disabled:opacity-50">Désactiver</button>
        </div>
      ) : enroll ? (
        <div className="flex flex-col gap-2">
          <p className="opacity-70">Scanne ce QR code avec ton app d'authentification (Google Authenticator, Authy…), puis saisis le code à 6 chiffres.</p>
          {enroll.qrCode && <img src={enroll.qrCode} alt="QR code 2FA" className="h-40 w-40 self-center" />}
          {enroll.secret && <p className="text-xs opacity-60 break-all">Clé manuelle : <code>{enroll.secret}</code></p>}
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            className="rounded border border-slate-300 px-3 py-2 tracking-widest"
          />
          <div className="flex gap-2">
            <button onClick={confirmEnroll} disabled={busy || code.length !== 6} className="rounded-md px-3 py-1.5 font-semibold text-white disabled:opacity-50" style={{ background: '#16A34A' }}>
              {busy ? 'Vérification…' : 'Confirmer'}
            </button>
            <button onClick={() => { setEnroll(null); setCode(''); }} className="rounded-md border border-slate-300 px-3 py-1.5 font-semibold">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="opacity-70">Non activée. Ajoute une couche de sécurité à ta connexion.</span>
          <button onClick={startEnroll} disabled={busy} className="rounded-md px-3 py-1.5 font-semibold text-white disabled:opacity-50" style={{ background: '#2563EB' }}>
            Activer
          </button>
        </div>
      )}
      {error && <p className="mt-2 text-red-600">{error}</p>}
      {msg && <p className="mt-2 text-green-700">{msg}</p>}
    </div>
  );
}
